import { useState, useEffect, useCallback, useRef } from 'react';
import { usePlatforms } from '../../../hooks/usePlatforms';
import { getAvailableDoors, getOcupationPercentage } from '../../../utils/lockerOccupancy';

const usePlatformsGeneral = () => {
  const { getAllPlatformsClient } = usePlatforms();
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const abortControllerRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedField, setSelectedField] = useState('locationName');
  const [allPlatformsRaw, setAllPlatformsRaw] = useState([]);
  const [filteredPlatforms, setFilteredPlatforms] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });

  const onRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    const loadPlatforms = async () => {
      setLoading(true);
      setError(null);

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const rawPlatforms = await getAllPlatformsClient(abortControllerRef.current.signal);

        if (!rawPlatforms || !Array.isArray(rawPlatforms)) {
          setPlatforms([]);
          setFilteredPlatforms([]);
          setTotalItems(0);
          setStats({ total: 0, online: 0, offline: 0 });
          return;
        }

        const platformsWithOcupation = rawPlatforms.map((platform) => {
          const availableDoors = getAvailableDoors(platform);
          const ocupationLocker = getOcupationPercentage(platform?.totalDoors, availableDoors);
          return { ...platform, ocupationLocker, availableDoors };
        });

        setAllPlatformsRaw(platformsWithOcupation);

        let filtered = [...platformsWithOcupation];

        if (filters.locationName && filters.locationName.trim()) {
          const term = filters.locationName.trim().toLowerCase();
          filtered = filtered.filter(
            (p) =>
              (p.locationName || '').toLowerCase().includes(term) ||
              (p.Plataforma || '').toLowerCase().includes(term)
          );
        }
        if (filters.locationId && filters.locationId.trim()) {
          const term = filters.locationId.trim().toUpperCase();
          filtered = filtered.filter(
            (p) =>
              (p.locationId || '').toUpperCase().includes(term) ||
              (p.locationId || '').includes(term)
          );
        }
        if (filters.zipCode && filters.zipCode.trim()) {
          const term = filters.zipCode.trim();
          filtered = filtered.filter((p) => (p.zipCode || '').includes(term));
        }
        if (filters.boxitLocationId && filters.boxitLocationId.trim()) {
          const term = String(filters.boxitLocationId).trim();
          filtered = filtered.filter((p) => String(p.boxitLocationId || '').includes(term));
        }
        if (filters.status === 'ONLINE') {
          filtered = filtered.filter((p) => p.locationStatus === 'ONLINE');
        } else if (filters.status === 'OFFLINE') {
          filtered = filtered.filter((p) => p.locationStatus !== 'ONLINE');
        }

        let sorted = filtered;
        if (sortConfig.key) {
          sorted = [...filtered].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
              aValue = aValue.toLowerCase();
              bValue = bValue.toLowerCase();
              return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
          });
        } else {
          sorted = [...filtered].sort((a, b) => (a.IdEstatus < b.IdEstatus ? 1 : -1));
        }

        setPlatforms(sorted);
        setFilteredPlatforms(sorted);
        setTotalItems(sorted.length);

        const onlineCount = filtered.filter((p) => p.locationStatus === 'ONLINE').length;
        const offlineCount = filtered.length - onlineCount;
        setStats({ total: filtered.length, online: onlineCount, offline: offlineCount });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading platforms:', err);
          setError(err.message || 'Error al cargar las plataformas');
          setPlatforms([]);
          setFilteredPlatforms([]);
          setTotalItems(0);
          setStats({ total: 0, online: 0, offline: 0 });
        }
      } finally {
        // Only the request that was not superseded may clear the flag. An
        // aborted one used to switch it off on its way out, so the skeleton
        // disappeared while its replacement was still in flight and the table
        // sat blank for a few hundred milliseconds before the rows landed.
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadPlatforms();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [getAllPlatformsClient, refreshKey, filters, sortConfig]);

  const handleSearch = useCallback((term, field = 'locationName') => {
    setSelectedField(field);
    setCurrentPage(1);
    if (term && term.trim()) {
      setFilters((prev) => ({ ...prev, [field]: term.trim() }));
    }
  }, []);

  const handleFilter = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((status) => {
    setCurrentPage(1);
    if (status && status !== 'all') {
      setFilters((prev) => ({ ...prev, status }));
    } else {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.status;
        return next;
      });
    }
  }, []);

  const handleSort = useCallback((key, direction) => {
    setSortConfig({ key, direction });
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPlatforms = platforms.slice(startIndex, startIndex + itemsPerPage);

  return {
    platforms: paginatedPlatforms,
    filteredPlatforms,
    platformArray: allPlatformsRaw,
    stats,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
    },
    filters,
    selectedField,
    handleSearch,
    handleFilter,
    handleStatusChange,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    onRefresh,
  };
};

export default usePlatformsGeneral;
